import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';
import dayjs from 'dayjs';

interface AttendanceTabProps {
  farmId: string;
}

interface AttendanceRecord {
  id: string;
  worker_id: string;
  date: string;
  status: string;
  notes?: string;
  worker_name?: string;
  check_in?: string;
  check_out?: string;
}

interface Worker {
  id: string;
  name: string;
}

const AttendanceTab: React.FC<AttendanceTabProps> = ({ farmId }) => {
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    worker_id: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    check_in: '',
    check_out: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch workers for this farm
      const workersData = await fetchWorkers(farmId);
      setWorkers(workersData);

      // Fetch attendance records for this farm
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('worker_attendance')
        .select(`
          *,
          workers!inner(name, farm_id)
        `)
        .eq('workers.farm_id', farmId)
        .order('date', { ascending: false });

      if (attendanceError) throw attendanceError;

      const formattedRecords = attendanceData?.map(record => ({
        ...record,
        worker_name: record.workers?.name || 'Unknown'
      })) || [];

      setAttendanceRecords(formattedRecords);
    } catch (err) {
      console.error('Error loading attendance data:', err);
      setError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttendance = async () => {
    try {
      setError(null);
      const payload: any = {
        worker_id: formData.worker_id,
        date: formData.date,
        status: formData.status,
        notes: formData.notes,
        farm_id: farmId,
      };
      if (formData.check_in) payload.check_in = `${formData.date}T${formData.check_in}:00`;
      if (formData.check_out) payload.check_out = `${formData.date}T${formData.check_out}:00`;
      const { error } = await supabase
        .from('worker_attendance')
        .insert([payload]);
      if (error) throw error;
      setDialogOpen(false);
      setFormData({
        worker_id: '',
        date: new Date().toISOString().split('T')[0],
        status: 'present',
        check_in: '',
        check_out: '',
        notes: ''
      });
      loadData();
    } catch (err) {
      console.error('Error adding attendance:', err);
      setError('Failed to add attendance record');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Worker Attendance</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add Attendance
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Check In</TableCell>
              <TableCell>Check Out</TableCell>
              <TableCell>Hours Worked</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendanceRecords.map((record) => {
              let hoursWorked = '';
              if (record.check_in && record.check_out) {
                const inTime = dayjs(record.check_in);
                const outTime = dayjs(record.check_out);
                const diff = outTime.diff(inTime, 'minute') / 60;
                hoursWorked = diff > 0 ? diff.toFixed(2) : '';
              }
              return (
                <TableRow key={record.id}>
                  <TableCell>{record.worker_name}</TableCell>
                  <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                  <TableCell>{record.status}</TableCell>
                  <TableCell>{record.check_in ? dayjs(record.check_in).format('HH:mm') : '-'}</TableCell>
                  <TableCell>{record.check_out ? dayjs(record.check_out).format('HH:mm') : '-'}</TableCell>
                  <TableCell>{hoursWorked}</TableCell>
                  <TableCell>{record.notes || '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Attendance Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={formData.worker_id}
              onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
              margin="normal"
              required
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              select
              label="Status"
              fullWidth
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="present">Present</MenuItem>
              <MenuItem value="absent">Absent</MenuItem>
              <MenuItem value="late">Late</MenuItem>
              <MenuItem value="on-leave">On Leave</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Check In"
                type="time"
                value={formData.check_in}
                onChange={e => setFormData({ ...formData, check_in: e.target.value })}
                margin="normal"
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Check Out"
                type="time"
                value={formData.check_out}
                onChange={e => setFormData({ ...formData, check_out: e.target.value })}
                margin="normal"
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAttendance} variant="contained">
            Add Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceTab;